import { zodResolver } from '@hookform/resolvers/zod';
import {
  customersControllerCreateContact,
  customersControllerDeleteContact,
  customersControllerFindOne,
  customersControllerGetDocumentUrl,
  customersControllerUpdate,
  customersControllerUpdateContact,
} from '@kalba/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useRouterState } from '@tanstack/react-router';
import {
  Activity,
  Briefcase,
  Building2,
  Calendar,
  Check,
  ChevronRight,
  ClipboardList,
  Download,
  Edit2,
  FileCheck,
  FileText,
  Hash,
  Loader2,
  Mail,
  MapPin,
  MoreHorizontal,
  Pencil,
  Phone,
  Plus,
  Star,
  Trash2,
  Upload,
  Users,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { AppLayout } from '../../../components/layout/app-layout';
import { Breadcrumb, buildDetailItems } from '../../../components/layout/breadcrumb';
import { GradientAvatar } from '../../../components/shared/gradient-avatar';
import { StatCard } from '../../../components/shared/stat-card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { PhoneInput } from '../../../components/ui/phone-input';
import { usePermissions } from '../../../hooks/use-permissions';
import { formatUaePhone } from '../../../lib/phone';
import { cn } from '../../../lib/utils';
import { type ContactFormValues, contactSchema } from '../../../lib/validations';
import { useAuthStore } from '../../../stores/auth.store';

// ── Route ─────────────────────────────────────────────────────────────────────

interface Contact {
  id: string;
  fullName: string;
  email: string;
  phone: string;
  designation?: string | null;
  isPrimary: boolean;
  userId?: string | null;
}

interface PermitSummary {
  id: string;
  referenceNo: string;
  status: string;
  createdAt: string;
  requestTypeConfig: { name: string };
}

interface CustomerDetail {
  id: string;
  name: string;
  accountType: string;
  status: string;
  sapCode?: string | null;
  primaryEmail?: string | null;
  primaryPhone?: string | null;
  tradeLicenseNo?: string | null;
  vatRegistrationNo?: string | null;
  tradeLicenseKey?: string | null;
  vatRegistrationKey?: string | null;
  createdAt: string;
  contacts: Contact[];
  requests: PermitSummary[];
}

export const Route = createFileRoute('/customers/$customerId/')({
  beforeLoad: () => {
    const { user, permissions } = useAuthStore.getState();
    if (!user) throw redirect({ to: '/login' });
    if (!permissions.includes('customer:view')) throw redirect({ to: '/dashboard' });
  },
  component: CustomerDetailPage,
});

// ── Constants ─────────────────────────────────────────────────────────────────

const TERMINAL = new Set(['REJECTED', 'PAID', 'PERMIT_ISSUED', 'SENT_TO_SAP']);

const PERMIT_STATUS: Record<string, { label: string; cls: string; progress: number; bar: string }> =
  {
    DRAFT: { label: 'Draft', cls: 'bg-gray-100 text-gray-600', progress: 5, bar: 'bg-gray-400' },
    SUBMITTED_FOR_INSPECTION: {
      label: 'Inspection',
      cls: 'bg-warning-50 text-warning-700',
      progress: 25,
      bar: 'bg-warning-500',
    },
    INSPECTION_IN_PROGRESS: {
      label: 'Inspection',
      cls: 'bg-warning-50 text-warning-700',
      progress: 40,
      bar: 'bg-warning-500',
    },
    PLEDGE_STUDY_REQUEST: {
      label: 'Pledge Study',
      cls: 'bg-brand-50 text-brand-600',
      progress: 50,
      bar: 'bg-brand-400',
    },
    APPROVAL_REQUEST: {
      label: 'Approval',
      cls: 'bg-brand-50 text-brand-600',
      progress: 60,
      bar: 'bg-brand-500',
    },
    APPROVED: {
      label: 'Approved',
      cls: 'bg-success-50 text-success-700',
      progress: 75,
      bar: 'bg-success-500',
    },
    REJECTED: {
      label: 'Rejected',
      cls: 'bg-error-50 text-error-700',
      progress: 0,
      bar: 'bg-error-500',
    },
    PERMIT_ISSUED: {
      label: 'Issued',
      cls: 'bg-success-50 text-success-700',
      progress: 100,
      bar: 'bg-success-500',
    },
    PAID: {
      label: 'Paid',
      cls: 'bg-success-50 text-success-700',
      progress: 100,
      bar: 'bg-success-500',
    },
    SENT_TO_SAP: {
      label: 'Sent to SAP',
      cls: 'bg-success-100 text-success-800',
      progress: 100,
      bar: 'bg-success-600',
    },
  };

// ── DocCard ───────────────────────────────────────────────────────────────────

interface DocCardDoc {
  label: string;
  url?: string;
  gradient: string;
}

function DocCard({ doc, onPreview }: { doc: DocCardDoc; onPreview: () => void }) {
  const [imgError, setImgError] = useState(false);

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-theme-sm hover:shadow-theme-md hover:border-brand-200 transition-all duration-200 overflow-hidden flex flex-col">
      {/* Thumbnail */}
      <button
        type="button"
        disabled={!doc.url}
        onClick={onPreview}
        className="relative w-full h-44 rounded-t-2xl overflow-hidden bg-gray-50 focus:outline-none group"
        aria-label={`Preview ${doc.label}`}
      >
        {doc.url && !imgError ? (
          <>
            <img
              src={doc.url}
              alt={doc.label}
              className="w-full h-full object-contain transition-transform duration-200 group-hover:scale-[1.02]"
              onError={() => setImgError(true)}
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-200 flex items-center justify-center">
              <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 rounded-full p-2 shadow-md">
                <FileText className="w-4 h-4 text-gray-700" />
              </div>
            </div>
          </>
        ) : (
          <div
            className={cn(
              'w-full h-full flex items-center justify-center bg-gradient-to-br',
              doc.gradient,
            )}
          >
            <FileText className="w-10 h-10 text-white/90" />
          </div>
        )}
      </button>

      {/* Label + Download */}
      <div className="px-3 py-3 flex flex-col gap-2">
        <p className="text-xs font-semibold text-gray-900 truncate">{doc.label}</p>
        {doc.url ? (
          <a
            href={doc.url}
            download
            className="flex items-center justify-center gap-1.5 w-full px-2 py-1.5 rounded-lg border border-gray-200 hover:bg-brand-50 hover:border-brand-200 hover:text-brand-600 text-xs font-medium text-gray-600 transition-all"
          >
            <Download className="w-3.5 h-3.5" /> Download
          </a>
        ) : (
          <div className="h-7 rounded-lg bg-gray-100 animate-pulse" />
        )}
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

function CustomerDetailPage() {
  const { customerId } = Route.useParams();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const qc = useQueryClient();
  const { location } = useRouterState();

  const {
    data: customer,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['customer', customerId],
    queryFn: () =>
      customersControllerFindOne({ id: customerId }) as unknown as Promise<CustomerDetail>,
  });

  const { data: tradeLicenseData } = useQuery({
    queryKey: ['customer-doc', customerId, 'TRADE_LICENSE'],
    queryFn: () =>
      customersControllerGetDocumentUrl({
        id: customerId,
        type: 'TRADE_LICENSE',
      }) as unknown as Promise<{ url: string }>,
    enabled: !!customer?.tradeLicenseKey,
    staleTime: 10 * 60 * 1000,
  });

  const { data: vatData } = useQuery({
    queryKey: ['customer-doc', customerId, 'VAT_REGISTRATION'],
    queryFn: () =>
      customersControllerGetDocumentUrl({
        id: customerId,
        type: 'VAT_REGISTRATION',
      }) as unknown as Promise<{ url: string }>,
    enabled: !!customer?.vatRegistrationKey,
    staleTime: 10 * 60 * 1000,
  });

  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [deactivateDialog, setDeactivateDialog] = useState(false);
  const [hoveredPermit, setHoveredPermit] = useState<string | null>(null);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [addingContact, setAddingContact] = useState(false);
  const [previewDoc, setPreviewDoc] = useState<{ label: string; url: string } | null>(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['customer', customerId] });

  const deactivateMutation = useMutation({
    mutationFn: () =>
      customersControllerUpdate({ id: customerId, requestBody: { status: 'INACTIVE' } }),
    onSuccess: () => {
      invalidate();
      setDeactivateDialog(false);
    },
  });

  const deleteContactMutation = useMutation({
    mutationFn: (contactId: string) =>
      customersControllerDeleteContact({ id: customerId, contactId }),
    onSuccess: () => {
      invalidate();
      setDeleteConfirm(null);
    },
  });

  const editContactForm = useForm<ContactFormValues>({
    resolver: zodResolver(contactSchema),
    defaultValues: { fullName: '', email: '', phone: '', designation: '', isPrimary: false },
  });

  const updateContactMutation = useMutation({
    mutationFn: (data: ContactFormValues) =>
      customersControllerUpdateContact({
        id: customerId,
        contactId: editingContact!.id,
        requestBody: {
          fullName: data.fullName.trim(),
          email: data.email?.trim() || undefined,
          phone: data.phone?.trim() || undefined,
          designation: data.designation?.trim() || undefined,
          isPrimary: data.isPrimary,
        },
      }),
    onSuccess: () => {
      invalidate();
      setEditingContact(null);
      editContactForm.reset();
    },
    onError: (err: unknown) => {
      const body = (err as { body?: { message?: string | string[] } })?.body;
      const raw = body?.message;
      const lines = Array.isArray(raw) ? raw : [raw ?? 'Failed to update contact.'];
      toast.error('Failed to update contact', { description: lines.join(' · '), duration: 5000 });
    },
  });

  const addContactForm = useForm<ContactFormValues>({
    resolver: zodResolver(contactSchema),
    defaultValues: { fullName: '', email: '', phone: '', designation: '', isPrimary: false },
  });

  const createContactMutation = useMutation({
    mutationFn: (data: ContactFormValues) =>
      customersControllerCreateContact({
        id: customerId,
        requestBody: {
          fullName: data.fullName.trim(),
          email: data.email?.trim() ?? '',
          phone: data.phone?.trim() ?? '',
          designation: data.designation?.trim() || undefined,
          isPrimary: data.isPrimary,
        },
      }),
    onSuccess: () => {
      invalidate();
      setAddingContact(false);
      addContactForm.reset();
    },
    onError: (err: unknown) => {
      const body = (err as { body?: { message?: string | string[] } })?.body;
      const raw = body?.message;
      const lines = Array.isArray(raw) ? raw : [raw ?? 'Failed to add contact.'];
      toast.error('Failed to add contact', { description: lines.join(' · '), duration: 5000 });
    },
  });

  const canEdit = can('customer:edit');

  const breadcrumbItems = buildDetailItems(location.pathname, customer?.name ?? '…', () =>
    navigate({ to: '/customers' }),
  );

  if (isLoading) {
    return (
      <AppLayout>
        <div className="space-y-4 animate-pulse">
          <div className="h-4 w-32 rounded bg-gray-100" />
          <div className="h-48 rounded-2xl bg-gray-100" />
          <div className="h-48 rounded-2xl bg-gray-100" />
        </div>
      </AppLayout>
    );
  }

  if (isError || !customer) {
    return (
      <AppLayout>
        <div className="py-20 text-center text-error-500">Customer not found.</div>
      </AppLayout>
    );
  }

  const activePermits = customer.requests.filter((p) => !TERMINAL.has(p.status));
  const docCount = (customer.tradeLicenseKey ? 1 : 0) + (customer.vatRegistrationKey ? 1 : 0);
  const isActive = customer.status === 'ACTIVE';
  const sinceYear = customer.createdAt ? new Date(customer.createdAt).getFullYear() : null;

  const docs = [
    customer.tradeLicenseKey && {
      label: 'Trade License',
      type: 'TRADE_LICENSE' as const,
      url: tradeLicenseData?.url,
      gradient: 'from-emerald-500 to-teal-600',
      colorBg: 'bg-emerald-50',
      colorText: 'text-emerald-700',
      colorBorder: 'border-emerald-200',
    },
    customer.vatRegistrationKey && {
      label: 'VAT Certificate',
      type: 'VAT_REGISTRATION' as const,
      url: vatData?.url,
      gradient: 'from-blue-500 to-indigo-600',
      colorBg: 'bg-blue-50',
      colorText: 'text-blue-700',
      colorBorder: 'border-blue-200',
    },
  ].filter(Boolean) as Array<{
    label: string;
    type: string;
    url?: string;
    gradient: string;
    colorBg: string;
    colorText: string;
    colorBorder: string;
  }>;

  return (
    <AppLayout>
      <Breadcrumb items={breadcrumbItems} />

      <div className="space-y-6 pb-10">
        {/* ── Hero ── */}
        <div className="relative bg-gradient-to-br from-white via-white to-brand-50/40 rounded-2xl border border-gray-200 shadow-theme-sm overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-brand-100/30 to-transparent rounded-full -translate-y-1/3 translate-x-1/4 pointer-events-none" />
          <div className="relative p-6 sm:p-8">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-5">
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center shadow-xl shrink-0">
                  <Building2 className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                </div>
                <div className="space-y-2 pt-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900">
                      {customer.name}
                    </h1>
                    <span
                      className={cn(
                        'inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border',
                        isActive
                          ? 'bg-success-50 text-success-700 border-success-200'
                          : 'bg-gray-100 text-gray-500 border-gray-200',
                      )}
                    >
                      <span
                        className={cn(
                          'w-1.5 h-1.5 rounded-full',
                          isActive ? 'bg-success-500 animate-pulse' : 'bg-gray-400',
                        )}
                      />
                      {isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 font-medium">{customer.accountType}</p>
                  <div className="flex items-center gap-4 flex-wrap">
                    {customer.sapCode && (
                      <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
                        <Hash className="w-3.5 h-3.5" />
                        {customer.sapCode}
                      </span>
                    )}
                    {customer.primaryEmail && (
                      <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
                        <Mail className="w-3.5 h-3.5" />
                        {customer.primaryEmail}
                      </span>
                    )}
                    {customer.primaryPhone && (
                      <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
                        <Phone className="w-3.5 h-3.5" />
                        {formatUaePhone(customer.primaryPhone)}
                      </span>
                    )}
                    {sinceYear && (
                      <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
                        <Calendar className="w-3.5 h-3.5" />
                        Customer since {sinceYear}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                {canEdit && (
                  <>
                    <button
                      type="button"
                      onClick={() =>
                        navigate({ to: '/customers/$customerId/edit', params: { customerId } })
                      }
                      className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors text-gray-700"
                    >
                      <Edit2 className="w-4 h-4" /> Edit Profile
                    </button>
                    {isActive && (
                      <button
                        type="button"
                        onClick={() => setDeactivateDialog(true)}
                        className="px-3 py-2 text-sm font-medium border border-error-200 text-error-600 rounded-xl hover:bg-error-50 transition-colors"
                      >
                        Deactivate
                      </button>
                    )}
                    <button
                      type="button"
                      className="p-2 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
                    >
                      <MoreHorizontal className="w-4 h-4 text-gray-500" />
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Stats row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
              <StatCard
                size="sm"
                icon={<ClipboardList />}
                iconBg="bg-blue-50"
                iconColor="text-blue-500"
                label="Total Permits"
                value={customer.requests.length}
              />
              <StatCard
                size="sm"
                icon={<Activity />}
                iconBg="bg-emerald-50"
                iconColor="text-emerald-500"
                label="Active Permits"
                value={activePermits.length}
              />
              <StatCard
                size="sm"
                icon={<MapPin />}
                iconBg="bg-amber-50"
                iconColor="text-amber-500"
                label="Contacts"
                value={customer.contacts.length}
              />
              <StatCard
                size="sm"
                icon={<FileCheck />}
                iconBg="bg-cyan-50"
                iconColor="text-cyan-500"
                label="Documents"
                value={docCount}
              />
            </div>
          </div>
        </div>

        {/* ── Contacts ── */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-theme-sm overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-warning-50 flex items-center justify-center">
                <Users className="w-4 h-4 text-warning-600" />
              </div>
              <h3 className="text-base font-semibold text-gray-900">Contacts</h3>
            </div>
            {canEdit && (
              <button
                type="button"
                onClick={() => setAddingContact(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors text-gray-700"
              >
                <Plus className="w-3.5 h-3.5" /> Add Contact
              </button>
            )}
          </div>

          {customer.contacts.length === 0 ? (
            <div className="px-6 py-8 text-center text-sm text-gray-400">
              No contacts added yet.
              {can('customer:create') && (
                <button
                  type="button"
                  onClick={() =>
                    navigate({ to: '/customers/$customerId/edit', params: { customerId } })
                  }
                  className="ml-2 text-brand-500 hover:text-brand-600 font-medium"
                >
                  Add one →
                </button>
              )}
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {customer.contacts.map((c) => (
                <div
                  key={c.id}
                  className="flex items-center gap-5 px-6 py-4 hover:bg-gray-50/60 transition-colors group"
                >
                  <GradientAvatar
                    name={c.fullName}
                    size="md"
                    shape="rounded"
                    className="shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <span className="font-semibold text-[15px] text-gray-900">{c.fullName}</span>
                      {c.isPrimary && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-brand-50 text-brand-600 border border-brand-100">
                          <Star className="w-2.5 h-2.5" /> Primary
                        </span>
                      )}
                      {c.userId ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-success-50 text-success-700 border border-success-100">
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500 border border-gray-200">
                          Inactive
                        </span>
                      )}
                    </div>
                    {c.designation && (
                      <div className="flex items-center gap-1 text-xs text-gray-400">
                        <Briefcase className="w-3 h-3" />
                        {c.designation}
                      </div>
                    )}
                  </div>
                  <div className="hidden lg:flex items-center gap-8 text-sm text-gray-500">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
                        <Mail className="w-3.5 h-3.5 text-gray-400" />
                      </div>
                      {c.email}
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
                        <Phone className="w-3.5 h-3.5 text-gray-400" />
                      </div>
                      {formatUaePhone(c.phone)}
                    </div>
                  </div>
                  {canEdit && (
                    <div className="shrink-0 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        type="button"
                        onClick={() => {
                          setEditingContact(c);
                          editContactForm.reset({
                            fullName: c.fullName,
                            email: c.email ?? '',
                            phone: formatUaePhone(c.phone ?? ''),
                            designation: c.designation ?? '',
                            isPrimary: c.isPrimary,
                          });
                        }}
                        className="w-8 h-8 rounded-lg hover:bg-brand-50 flex items-center justify-center transition-colors"
                      >
                        <Pencil className="w-3.5 h-3.5 text-gray-400 hover:text-brand-600" />
                      </button>
                      {deleteConfirm === c.id ? (
                        <div className="flex items-center gap-1 text-xs">
                          <span className="text-gray-400">Delete?</span>
                          <button
                            type="button"
                            onClick={() => deleteContactMutation.mutate(c.id)}
                            className="text-error-600 font-semibold px-1.5 hover:text-error-700"
                          >
                            Yes
                          </button>
                          <button
                            type="button"
                            onClick={() => setDeleteConfirm(null)}
                            className="text-gray-400 px-1.5"
                          >
                            No
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setDeleteConfirm(c.id)}
                          className="w-8 h-8 rounded-lg hover:bg-error-50 flex items-center justify-center transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5 text-gray-400 hover:text-error-600" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Edit Contact Dialog ── */}
        <Dialog
          open={!!editingContact}
          onOpenChange={(open) => {
            if (!open) {
              setEditingContact(null);
              editContactForm.reset();
            }
          }}
        >
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Edit Contact</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 pt-2">
              <div>
                <label className="text-xs font-semibold text-gray-500 mb-1 block">
                  Full Name <span className="text-error-500">*</span>
                </label>
                <Input
                  {...editContactForm.register('fullName')}
                  type="text"
                  placeholder="First and last name"
                  hasError={!!editContactForm.formState.errors.fullName}
                />
                {editContactForm.formState.errors.fullName && (
                  <p className="mt-1 text-xs text-error-600">
                    {editContactForm.formState.errors.fullName.message}
                  </p>
                )}
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 mb-1 block">
                  Role / Title
                </label>
                <Input
                  {...editContactForm.register('designation')}
                  type="text"
                  placeholder="e.g. Project Manager"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-500 mb-1 block">Email</label>
                  <Input
                    {...editContactForm.register('email')}
                    type="email"
                    placeholder="contact@company.com"
                    hasError={!!editContactForm.formState.errors.email}
                  />
                  {editContactForm.formState.errors.email && (
                    <p className="mt-1 text-xs text-error-600">
                      {editContactForm.formState.errors.email.message}
                    </p>
                  )}
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 mb-1 block">Phone</label>
                  <PhoneInput
                    {...editContactForm.register('phone')}
                    placeholder="+971 50 123 4567"
                    hasError={!!editContactForm.formState.errors.phone}
                  />
                  {editContactForm.formState.errors.phone && (
                    <p className="mt-1 text-xs text-error-600">
                      {editContactForm.formState.errors.phone.message}
                    </p>
                  )}
                </div>
              </div>
              {editingContact && !editingContact.isPrimary && (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    {...editContactForm.register('isPrimary')}
                    className="w-4 h-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500"
                  />
                  <span className="text-xs font-medium text-gray-700">Make primary contact</span>
                </label>
              )}
              <div className="flex items-center gap-2 pt-1">
                <button
                  type="button"
                  onClick={editContactForm.handleSubmit((data) =>
                    updateContactMutation.mutate(data),
                  )}
                  disabled={updateContactMutation.isPending}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-semibold rounded-xl bg-brand-500 text-white hover:bg-brand-600 transition-all disabled:opacity-50"
                >
                  {updateContactMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Check className="w-4 h-4" />
                  )}
                  Save Changes
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEditingContact(null);
                    editContactForm.reset();
                  }}
                  className="px-4 py-2.5 text-sm font-medium rounded-xl border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* ── Add Contact Dialog ── */}
        <Dialog
          open={addingContact}
          onOpenChange={(open) => {
            if (!open) {
              setAddingContact(false);
              addContactForm.reset();
            }
          }}
        >
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Add Contact</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 pt-2">
              <div>
                <label className="text-xs font-semibold text-gray-500 mb-1 block">
                  Full Name <span className="text-error-500">*</span>
                </label>
                <Input
                  {...addContactForm.register('fullName')}
                  type="text"
                  placeholder="First and last name"
                  hasError={!!addContactForm.formState.errors.fullName}
                />
                {addContactForm.formState.errors.fullName && (
                  <p className="mt-1 text-xs text-error-600">
                    {addContactForm.formState.errors.fullName.message}
                  </p>
                )}
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 mb-1 block">
                  Role / Title
                </label>
                <Input
                  {...addContactForm.register('designation')}
                  type="text"
                  placeholder="e.g. Project Manager"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-500 mb-1 block">
                    Email <span className="text-error-500">*</span>
                  </label>
                  <Input
                    {...addContactForm.register('email')}
                    type="email"
                    placeholder="contact@company.com"
                    hasError={!!addContactForm.formState.errors.email}
                  />
                  {addContactForm.formState.errors.email && (
                    <p className="mt-1 text-xs text-error-600">
                      {addContactForm.formState.errors.email.message}
                    </p>
                  )}
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 mb-1 block">
                    Phone <span className="text-error-500">*</span>
                  </label>
                  <PhoneInput
                    {...addContactForm.register('phone')}
                    placeholder="+971 50 123 4567"
                    hasError={!!addContactForm.formState.errors.phone}
                  />
                  {addContactForm.formState.errors.phone && (
                    <p className="mt-1 text-xs text-error-600">
                      {addContactForm.formState.errors.phone.message}
                    </p>
                  )}
                </div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  {...addContactForm.register('isPrimary')}
                  className="w-4 h-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500"
                />
                <span className="text-xs font-medium text-gray-700">Make primary contact</span>
              </label>
              <div className="flex items-center gap-2 pt-1">
                <button
                  type="button"
                  onClick={addContactForm.handleSubmit((data) =>
                    createContactMutation.mutate(data),
                  )}
                  disabled={createContactMutation.isPending}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-semibold rounded-xl bg-brand-500 text-white hover:bg-brand-600 transition-all disabled:opacity-50"
                >
                  {createContactMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Plus className="w-4 h-4" />
                  )}
                  Add Contact
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setAddingContact(false);
                    addContactForm.reset();
                  }}
                  className="px-4 py-2.5 text-sm font-medium rounded-xl border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* ── Documents ── */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Documents</h2>
              <p className="text-sm text-gray-500 mt-0.5">
                Uploaded compliance and legal documents
              </p>
            </div>
            {canEdit && (
              <button
                type="button"
                className="inline-flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors text-sm font-medium text-gray-700"
              >
                <Upload className="w-4 h-4" /> Upload Document
              </button>
            )}
          </div>

          {(customer.tradeLicenseNo || customer.vatRegistrationNo) && (
            <div className="grid grid-cols-2 gap-4">
              {customer.tradeLicenseNo && (
                <div className="rounded-2xl border border-gray-200 bg-white px-4 py-3">
                  <p className="text-xs font-medium text-gray-400">Trade License Number</p>
                  <p className="text-sm font-semibold text-gray-900 mt-0.5">
                    {customer.tradeLicenseNo}
                  </p>
                </div>
              )}
              {customer.vatRegistrationNo && (
                <div className="rounded-2xl border border-gray-200 bg-white px-4 py-3">
                  <p className="text-xs font-medium text-gray-400">VAT Registration Number</p>
                  <p className="text-sm font-semibold text-gray-900 mt-0.5">
                    {customer.vatRegistrationNo}
                  </p>
                </div>
              )}
            </div>
          )}

          {docs.length === 0 ? (
            <div className="bg-white rounded-2xl border border-dashed border-gray-200 px-6 py-8 text-center text-sm text-gray-400">
              No documents uploaded yet.
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              {docs.map((doc) => (
                <DocCard
                  key={doc.type}
                  doc={doc}
                  onPreview={() => setPreviewDoc({ label: doc.label, url: doc.url! })}
                />
              ))}
            </div>
          )}
        </div>

        {/* ── Permit Requests ── */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Permit Requests</h2>
              <p className="text-sm text-gray-500 mt-0.5">
                {customer.requests.length} permit{' '}
                {customer.requests.length === 1 ? 'application' : 'applications'} on record
              </p>
            </div>
            {(() => {
              const primaryContact =
                customer.contacts.find((c) => c.isPrimary) ?? customer.contacts[0];
              return (
                <Link
                  to="/permits/new"
                  search={{
                    accountId: customer.id,
                    accountName: customer.name,
                    ...(primaryContact
                      ? { contactId: primaryContact.id, contactName: primaryContact.fullName }
                      : {}),
                  }}
                  className="inline-flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors text-sm font-medium text-gray-700"
                >
                  <Plus className="w-4 h-4" /> New Permit
                </Link>
              );
            })()}
          </div>

          {customer.requests.length === 0 ? (
            <div className="bg-white rounded-2xl border border-dashed border-gray-200 px-6 py-8 text-center text-sm text-gray-400">
              No permit requests on record.
            </div>
          ) : (
            <div className="space-y-2.5">
              {customer.requests.slice(0, 5).map((p) => {
                const cfg = PERMIT_STATUS[p.status] ?? {
                  label: p.status,
                  cls: 'bg-gray-100 text-gray-600',
                  progress: 0,
                  bar: 'bg-gray-400',
                };
                return (
                  <Link
                    key={p.id}
                    to="/permits/$permitId"
                    params={{ permitId: p.id }}
                    onMouseEnter={() => setHoveredPermit(p.id)}
                    onMouseLeave={() => setHoveredPermit(null)}
                    className="flex items-center gap-5 bg-white rounded-2xl border border-gray-200 hover:border-brand-200 hover:shadow-theme-sm shadow-theme-sm transition-all duration-200 px-5 py-4"
                  >
                    <div className="w-10 h-10 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-center shrink-0">
                      <FileCheck className="w-4 h-4 text-gray-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2.5 mb-1">
                        <span className="font-mono text-xs font-semibold text-gray-400 tracking-wider">
                          {p.referenceNo}
                        </span>
                        <span
                          className={cn(
                            'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold',
                            cfg.cls,
                          )}
                        >
                          {cfg.label}
                        </span>
                      </div>
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {p.requestTypeConfig?.name}
                      </p>
                    </div>
                    <div className="hidden sm:flex items-center gap-1.5 text-xs text-gray-400 shrink-0">
                      <Calendar className="w-3.5 h-3.5" />
                      {new Date(p.createdAt).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </div>
                    <div className="hidden md:flex items-center gap-3 shrink-0 w-32">
                      <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={cn('h-full rounded-full transition-all duration-500', cfg.bar)}
                          style={{ width: `${cfg.progress}%` }}
                        />
                      </div>
                      <span className="text-xs text-gray-400 font-medium w-8 text-right">
                        {cfg.progress}%
                      </span>
                    </div>
                    <ChevronRight
                      className={cn(
                        'w-4 h-4 text-gray-400 transition-all duration-150',
                        hoveredPermit === p.id && 'translate-x-0.5 text-brand-500',
                      )}
                    />
                  </Link>
                );
              })}
            </div>
          )}

          {customer.requests.length > 0 && (
            <div className="text-center pt-1">
              <Link
                to="/permits"
                search={{ page: 1, limit: 20, accountId: customer.id, accountName: customer.name }}
                className="text-sm text-gray-500 hover:text-gray-900 transition-colors inline-flex items-center gap-1.5"
              >
                View all permits for {customer.name} <ChevronRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* ── Document Preview Modal ── */}
      {previewDoc && (
        <div className="fixed inset-0 z-[100000] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-md"
            onClick={() => setPreviewDoc(null)}
          />
          <div className="relative z-10 flex flex-col w-full max-w-3xl max-h-[90vh] rounded-2xl bg-white shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
              <div>
                <p className="text-sm font-semibold text-gray-900">{previewDoc.label}</p>
              </div>
              <div className="flex items-center gap-3">
                <a
                  href={previewDoc.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs font-medium text-brand-600 hover:text-brand-700 underline"
                >
                  Open in new tab
                </a>
                <button
                  type="button"
                  onClick={() => setPreviewDoc(null)}
                  className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto bg-gray-50 flex items-center justify-center p-4">
              <iframe
                src={previewDoc.url}
                title={previewDoc.label}
                className="w-full h-[70vh] rounded border-0"
              />
            </div>
          </div>
        </div>
      )}

      {/* ── Deactivate Dialog ── */}
      {deactivateDialog && (
        <div className="fixed inset-0 z-[100000] flex items-center justify-center bg-foreground/15 backdrop-blur-md">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl">
            <h3 className="mb-2 text-sm font-semibold text-gray-900">Deactivate Customer?</h3>
            <p className="mb-6 text-sm text-gray-500">
              This sets the account to Inactive. Blocked if there are active permit requests.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDeactivateDialog(false)}
                className="px-4 py-2 text-sm font-medium border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors text-gray-600"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => deactivateMutation.mutate()}
                disabled={deactivateMutation.isPending}
                className="px-4 py-2 text-sm font-semibold rounded-xl bg-error-500 text-white hover:bg-error-600 transition-colors disabled:opacity-50"
              >
                Deactivate
              </button>
            </div>
            {deactivateMutation.isError && (
              <p className="mt-2 text-xs text-error-600">
                Deactivation failed. May have active permits.
              </p>
            )}
          </div>
        </div>
      )}
    </AppLayout>
  );
}
